import { useServices } from "contexts/ServiceContext";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import useTranslation from "translations/useTranslation";
import Layout from "./Layout";
import banner from "./img/banner.jpg";
import { useShouldShowBanner } from "hooks/useShouldShowBanner";
import SavedPageCard from "./Card/SavedPageCard";
import { SavedPage } from "types/SavedPage";
import Selectors, {
    SelectorVariant,
} from "@tiendeo/react-components/dist/atoms/Selectors/Selectors";
import useGQLOperation from "hooks/useGQLOperation";
import deleteSavedPage from "services/gql/mutations/deleteSavedPage";
import { DeleteSavedPageMutation, DeleteSavedPageMutationVariables } from "graphql/schema-types";
import { getPlatform } from "@tiendeo/js-stats-client";
import { useFirstRender } from "hooks/useFirstRender";
import { ProfilePageTypes } from "enums/ProfilePageTypes";

const BannerContent = styled.div`
    margin-top: 25px;
    display: flex;
    justify-content: center;
    @media (min-width: ${(props) => props.theme.layout.breakpoints.md}) {
        margin-left: ${(props) => props.theme.spacing.sm};
        margin-top: 85px;
    }
`;

const MainContent = styled.div`
    display: flex;
    flex-direction: column;
    @media (min-width: ${(props) => props.theme.layout.breakpoints.md}) {
        min-width: 400px;
    }
    @media (min-width: ${(props) => props.theme.layout.breakpoints.lg}) {
        min-width: 600px;
    }
`;

const SavedPagesContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    @media (min-width: ${(props) => props.theme.layout.breakpoints.md}) {
        flex-direction: row;
    }
`;

const TitleSection = styled.h2`
    font-size: 20px;
    margin: ${(props) => props.theme.spacing.sm} 0;

    @media (min-width: ${(props) => props.theme.layout.breakpoints.md}) {
        margin: 0;
    }
`;

const CardsSection = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    background-color: ${(props) => props.theme.colors.neutral.neutral100};
    align-items: center;
    border-radius: 10px;
    gap: 16px;
    padding: 32px;
    margin-top: 20px;
    @media (min-width: ${(props) => props.theme.layout.breakpoints.xs}) {
        margin: 0;
    }
`;

const SelectorsSection = styled.div`
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    @media (min-width: ${(props) => props.theme.layout.breakpoints.xs}) {
        height: 60px;
    }
`;

const EmptySpace = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    background-color: ${(props) => props.theme.colors.neutral.white};
    align-items: center;
    border-radius: 10px;
    gap: 16px;
    padding: 32px;
    margin-top: 15px;
`;

const Message = styled.p`
    font-size: ${(props) => props.theme.typography.body.normal.fontSize};
    line-height: ${(props) => props.theme.typography.body.normal.lineHeight};
    font-weight: ${(props) => props.theme.typography.body.normal.fontWeight};
    color: ${(props) => props.theme.colors.neutral.neutral400};
`;

interface SavedPageContainerProps {
    savedPages: SavedPage[];
}

const SavedPagesContainer = ({ savedPages }: SavedPageContainerProps) => {
    const t = useTranslation();
    const shouldShowBanner = useShouldShowBanner();
    const { CookiesClient, TiendeoStatsClient, AuthApiClient, Configs } = useServices();
    const [myPages, setMyPages] = useState(savedPages);
    const [activeRetailerId, setActiveRetailerId] = useState<number>();
    const [oldRetailer, setOldRetailer] = useState<SavedPage["retailer"]>();
    const firstRender = useFirstRender();
    const [deleteSavedPageMutation] = useGQLOperation<
        DeleteSavedPageMutation,
        DeleteSavedPageMutationVariables
    >(deleteSavedPage);
    const haveSavedPages = myPages.length > 0;
    const filteredPages = myPages.filter((savedPage) =>
        activeRetailerId ? savedPage.retailer.id === activeRetailerId : myPages,
    );

    async function handleDeleteSavedPage({ id, page }: { id: number; page: number }) {
        const input = {
            catalogId: id,
            page,
        };

        const response = await deleteSavedPageMutation({ input });

        if (!response.DeleteSavedPage) return;

        const savedPageToDelete = `${id}|${page}`;
        const oldCookiesArray = CookiesClient.Get("favs").split("#");
        const cookiesLeft = oldCookiesArray.filter((cookie) => cookie !== savedPageToDelete);
        if (cookiesLeft.length === 0) {
            CookiesClient.Delete("favs");
        } else {
            const newCookie = cookiesLeft.join("#");
            CookiesClient.Set("favs", newCookie, 90 * 24);
        }

        const pageToDelete = myPages.find(
            (savedPage) => savedPage.id === id && savedPage.page === page,
        );
        const newPages = myPages.filter((savedPage) => savedPage !== pageToDelete);

        const eventPayload = {
            Action: "unsave",
            CatalogId: id,
            PageNumber: page,
            SearchType: "PROFILE",
            SearchWord: "profile",
            SearchCity: "no_city",
            View: "offers-search",
            PageType: ProfilePageTypes.SAVED,
        };
        await TiendeoStatsClient.Event(
            "Page/Action",
            AuthApiClient.GetUserId(),
            "",
            getPlatform(window.navigator.userAgent),
            eventPayload,
        );

        setMyPages(newPages);
    }

    async function launchFilterEvent() {
        const selectedRetailer = myPages.find((savedPage) => {
            if (savedPage.retailer.id === activeRetailerId) {
                return savedPage;
            }
        });

        let eventPayload = {};

        if (activeRetailerId !== undefined && activeRetailerId === selectedRetailer?.retailer?.id) {
            eventPayload = {
                Action: "filter",
                RetailerId: selectedRetailer?.retailer?.id,
                RetailerName: selectedRetailer?.retailer?.name,
                SearchType: "PROFILE",
                SearchWord: "profile",
                SearchCity: "no_city",
                View: "offers-search",
                PageType: ProfilePageTypes.SAVED,
            };
            await TiendeoStatsClient.Event(
                "Page/Action",
                AuthApiClient.GetUserId(),
                "",
                getPlatform(window.navigator.userAgent),
                eventPayload,
            );

            setOldRetailer(selectedRetailer?.retailer);
        }

        if (activeRetailerId === undefined && oldRetailer !== undefined) {
            eventPayload = {
                Action: "unfilter",
                RetailerId: oldRetailer?.id,
                RetailerName: oldRetailer?.name,
                SearchType: "PROFILE",
                SearchWord: "profile",
                SearchCity: "no_city",
                View: "offers-search",
                PageType: ProfilePageTypes.SAVED,
            };
            await TiendeoStatsClient.Event(
                "Page/Action",
                AuthApiClient.GetUserId(),
                "",
                getPlatform(window.navigator.userAgent),
                eventPayload,
            );
        }
    }

    const retailerIds = myPages.map((savedPage) => savedPage.retailer.id);

    const uniqueRetailers = myPages
        .filter((savedPage, index) => !retailerIds.includes(savedPage.retailer.id, index + 1)) //this functions filter duplicated retailers;
        .map((savedPage) => savedPage.retailer);

    useEffect(() => {
        launchFilterEvent();
    }, [activeRetailerId, launchFilterEvent]);

    const view = "offers-search";

    return (
        <Layout>
            <SavedPagesContentWrapper
                role="region"
                data-view={view}
                className="offers js-print-container"
            >
                <MainContent>
                    <TitleSection>{t("YOUR_SAVED_OFFERS")}</TitleSection>
                    {haveSavedPages && (
                        <SelectorsSection>
                            <Selectors
                                selectors={uniqueRetailers.map((retailer) => ({
                                    text: retailer.name,
                                    onClick: () => {
                                        setActiveRetailerId(
                                            activeRetailerId === retailer.id
                                                ? undefined
                                                : retailer.id,
                                        );
                                    },
                                    variant:
                                        activeRetailerId === retailer.id
                                            ? SelectorVariant.SOLID
                                            : SelectorVariant.OUTLINE,
                                }))}
                            />
                        </SelectorsSection>
                    )}
                    {!haveSavedPages && (
                        <EmptySpace>
                            <Message>{t("SAVED_OFFERS_CATALOGUE")}</Message>
                        </EmptySpace>
                    )}
                    {filteredPages.length > 0 && (
                        <CardsSection>
                            {filteredPages.map((savedPage: SavedPage, index) => {
                                return (
                                    <SavedPageCard
                                        savedPage={savedPage}
                                        key={savedPage.id + Math.random() * 100}
                                        handleDeleteSavedPage={() =>
                                            handleDeleteSavedPage(savedPage)
                                        }
                                        position={index}
                                    />
                                );
                            })}
                        </CardsSection>
                    )}
                </MainContent>
                {shouldShowBanner && (
                    <BannerContent>
                        <a
                            href={`${Configs.url_apps}?source=perfil&medium=web`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <img src={banner as any} width={287} height={455} alt="app banner" />
                        </a>
                    </BannerContent>
                )}
            </SavedPagesContentWrapper>
        </Layout>
    );
};

export default SavedPagesContainer;
